









































































import { Component } from "vue-property-decorator";
import Header from "@/components/designer/header/Header.vue";
import { User, UserProfileSchema } from "@/models/user/user";
import UserService from "@/services/user_service";
import AuthService from "@/services/auth_service";
import Share from "@/components/share/Share.vue";
import {
  AuthError,
  GenericError,
  PermissionsError,
} from "@/services/error_service";
import { mixins } from "vue-class-component";
import { ConfigCreator } from "@/mixins/configurator";
import { Auth } from "@/mixins/auth";

/**
 * Customer discount factors carry up to 4 decimal places (e.g. 0.3645), so the
 * MSRP Multiplier accepts that precision and rejects anything finer.
 */
const MSRP_MULTIPLIER_MIN = 0.0001;
const MSRP_MULTIPLIER_MAX = 10;
const MSRP_MULTIPLIER_MAX_DECIMALS = 4;

@Component({
  components: { Header, Share },
})
export default class UserProfile extends mixins(ConfigCreator, Auth) {
  protected userService = new UserService();
  protected authService = new AuthService();
  protected pwRequested = false;
  protected firstName = "";
  protected lastName = "";
  protected nameRules = [
    (v: string) => !!v || "Name is required",
    (v: string) => /^[\p{L} ,.'-]+$/u.test(v) || "Only letters allowed in name",
  ];
  protected email = "";
  protected emailRules = [
    (v: string) => !!v || "E-mail is required",
    (v: string) => /.+@.+/.test(v) || "E-mail must be valid",
  ];
  protected company = "";
  protected companyRules = [
    (v: string) => !!v || "Company is required",
    (v: string) => /^[\p{L} ,.'-]+$/u.test(v) || "Only letters allowed in company",
  ];
  protected msrpMultiplierValue = 1;
  protected msrpMultiplierRules = [
    (v: number | string) =>
      (v !== "" && v !== null && v !== undefined) ||
      "MSRP Multiplier is required",
    (v: number | string) =>
      Number.isFinite(Number(v)) || "MSRP Multiplier must be a number",
    (v: number | string) =>
      Number(v) >= MSRP_MULTIPLIER_MIN ||
      `MSRP Multiplier must be at least ${MSRP_MULTIPLIER_MIN}`,
    (v: number | string) =>
      Number(v) <= MSRP_MULTIPLIER_MAX ||
      `MSRP Multiplier must be ${MSRP_MULTIPLIER_MAX} or less`,
    (v: number | string) =>
      (String(v).split(".")[1] || "").length <= MSRP_MULTIPLIER_MAX_DECIMALS ||
      `MSRP Multiplier allows up to ${MSRP_MULTIPLIER_MAX_DECIMALS} decimal places`,
  ];

  async created(): Promise<void> {
    const loader = this.$loading.show();
    try {
      await this.checkAuth();
      await this.getUserInfo();
    } catch (err) {
      GenericError.popup(err.message);
    } finally {
      loader.hide();
    }
  }

  public async getUserInfo(): Promise<void> {
    try {
      this.firstName = this.user.first_name;
      this.lastName = this.user.last_name;
      this.email = this.user.email;
      this.company = this.user.customer_info?.company || "";
      this.msrpMultiplierValue = this.user.customer_info?.msrp_multiplier || 1;
    } catch (err) {
      if (err instanceof PermissionsError) {
        PermissionsError.popup(err.message, err.statusCode);
      } else if (err instanceof AuthError) {
        AuthError.logout();
      } else {
        GenericError.popup(err.message);
      }
    }
  }

  public async requestNewPassword(): Promise<void> {
    window.location.replace(
      `${process.env.VUE_APP_LOGIN_URL}/request-email?request=password`
    );
  }

  protected get msrpMultiplierError(): string | null {
    for (const rule of this.msrpMultiplierRules) {
      const result = rule(this.msrpMultiplierValue);
      if (result !== true) {
        return result as string;
      }
    }
    return null;
  }

  protected async submitProfile(): Promise<void> {
    if (this.msrpMultiplierError) {
      GenericError.popup(this.msrpMultiplierError);
      return;
    }
    let loader = this.$loading.show();
    const body = {
      first_name: this.firstName,
      last_name: this.lastName,
      email: this.email,
      customer_info: {
        company: this.company,
        msrp_multiplier: this.msrpMultiplierValue
      }
    } as UserProfileSchema;
    try {
      await this.userService.updateUserProfile(body);
      await this.setUserProfileInfo(body);
      loader.hide();
      loader = this.$loading.show(
        {},
        { before: this.$createElement("h1", "Saved!") }
      );
      setTimeout(() => loader.hide(), 750);
    } catch (err) {
      loader.hide();
      if (err instanceof PermissionsError) {
        PermissionsError.popup(err.message, err.statusCode);
      } else if (err instanceof AuthError) {
        AuthError.logout();
      } else {
        GenericError.popup(err.message);
      }
    }
  }
}
